import { createTamagui, createTokens, createFont } from 'tamagui'

// Design tokens based on Kinetic Typography design system
export const tokens = createTokens({
  color: {
    // Core colors
    background: '#09090B',
    backgroundMuted: '#27272A',
    foreground: '#FAFAFA',
    text: '#FAFAFA',
    textMuted: '#A1A1AA',
    accent: '#FFC73C',
    accentForeground: '#000000',
    border: '#3F3F46',
    
    // Semantic colors
    success: '#4ADE80',
    error: '#FCA5A5',
    warning: '#FBBF24',
    info: '#93C5FD',
    
    // Transparent variants
    transparent: 'transparent',
  },
  space: {
    0: 0,
    0.5: 2,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    true: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
    24: 96,
  },
  size: {
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    true: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
    24: 96,
  },
  radius: {
    0: 0,
    true: 0, // Brutalist - no border radius by default
    1: 2,
    2: 4,
    3: 6,
    4: 8,
  },
  zIndex: {
    0: 0,
    1: 100,
    2: 200,
    3: 300,
    4: 400,
    5: 500,
  },
})

// Space Grotesk / Inter font configuration
const headingFont = createFont({
  family: 'SpaceGrotesk, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  size: {
    1: 12,
    2: 14,
    3: 16,
    4: 18,
    5: 20,
    6: 24,
    7: 28,
    8: 32,
    9: 40,
    10: 48,
    11: 64,
    12: 80,
    13: 96,
    14: 128,
    true: 24,
  },
  lineHeight: {
    1: 16,
    2: 18,
    3: 20,
    4: 24,
    5: 28,
    6: 32,
    7: 36,
    8: 40,
    9: 48,
    10: 56,
    11: 72,
    12: 88,
    13: 104,
    14: 140,
    true: 32,
  },
  weight: {
    1: '400',
    4: '400',
    5: '500',
    6: '600',
    7: '700',
    8: '800',
    9: '900',
  },
  letterSpacing: {
    1: 0,
    2: -0.5,
    3: -1,
    4: -1.5,
    5: -2,
    6: -2.5,
    true: -0.5,
  },
  face: {
    400: { normal: 'SpaceGrotesk' },
    500: { normal: 'SpaceGrotesk' },
    600: { normal: 'SpaceGroteskBold' },
    700: { normal: 'SpaceGroteskBold' },
    800: { normal: 'SpaceGroteskBold' },
    900: { normal: 'SpaceGroteskBold' },
  },
})

const bodyFont = createFont({
  family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  size: {
    1: 11,
    2: 12,
    3: 13,
    4: 14,
    5: 16,
    6: 18,
    7: 20,
    8: 24,
    true: 14,
  },
  lineHeight: {
    1: 15,
    2: 17,
    3: 18,
    4: 20,
    5: 22,
    6: 24,
    7: 28,
    8: 32,
    true: 20,
  },
  weight: {
    1: '400',
    4: '400',
    5: '500',
    6: '600',
    7: '700',
  },
  letterSpacing: {
    1: 0,
    2: 0.25,
    3: 0.5,
    true: 0,
  },
  face: {
    400: { normal: 'Inter' },
    500: { normal: 'Inter' },
    600: { normal: 'InterBold' },
    700: { normal: 'InterBold' },
  },
})

const monoFont = createFont({
  family: 'JetBrainsMono, monospace',
  size: {
    1: 11,
    2: 12,
    3: 13,
    4: 14,
    5: 16,
    true: 12,
  },
  lineHeight: {
    1: 15,
    2: 17,
    3: 18,
    4: 20,
    5: 22,
    true: 17,
  },
  weight: {
    4: '400',
    5: '500',
    6: '600',
  },
  letterSpacing: {
    1: 0,
    2: 1,
    3: 2,
    true: 0,
  },
})

export const sakuraConfig = createTamagui({
  tokens,
  fonts: {
    heading: headingFont,
    body: bodyFont,
    mono: monoFont,
  },
  media: {
    xs: { maxWidth: 480 },
    sm: { maxWidth: 640 },
    md: { maxWidth: 768 },
    lg: { maxWidth: 1024 },
    xl: { maxWidth: 1280 },
    xxl: { maxWidth: 1536 },
    gtXs: { minWidth: 481 },
    gtSm: { minWidth: 641 },
    gtMd: { minWidth: 769 },
    gtLg: { minWidth: 1025 },
    gtXl: { minWidth: 1281 },
    short: { maxHeight: 820 },
    tall: { minHeight: 821 },
    hoverNone: { hover: 'none' },
    pointerCoarse: { pointer: 'coarse' },
  },
  themes: {
    dark: {
      background: tokens.color.background,
      backgroundMuted: tokens.color.backgroundMuted,
      color: tokens.color.foreground,
      colorMuted: tokens.color.textMuted,
      accent: tokens.color.accent,
      accentForeground: tokens.color.accentForeground,
      borderColor: tokens.color.border,
      success: '#14532D',
      error: '#991B1B',
      warning: '#78350F',
      info: '#1E40AF',
    },
    light: {
      background: '#FFFFFF',
      backgroundMuted: '#F4F4F5',
      color: '#09090B',
      colorMuted: '#71717A',
      accent: tokens.color.accent,
      accentForeground: tokens.color.accentForeground,
      borderColor: '#E4E4E7',
      success: tokens.color.success,
      error: tokens.color.error,
      warning: tokens.color.warning,
      info: tokens.color.info,
    },
  },
})

export type SakuraConfig = typeof sakuraConfig
export default sakuraConfig
